import * as React from 'react';
import moment from 'moment';
import { Box, Button, Checkbox, Chip, FormControlLabel, IconButton, Popover, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DateRangeCalendar } from '@mui/x-date-pickers-pro/DateRangeCalendar';
import { ReactComponent as CalendarIcon } from '../icons/calendar.svg';
import { cv } from '../../themeVars';

// The left panel's calendar button. "Departs on" puts one day into the search box as a
// departure date (the chat search's date handling). "Active during" filters both chat
// lists to guests whose tour or hotel stay overlaps the picked days, by each guest's own
// booking and each tour's own calendar days (backend: apiv2/lib/chat_active_during.py).
//
// includeLastDay: a guest on the last day of a hotel stay or multi-day tour is still
// around that day, so it is on by default. Off, they are left out. A day tour's only
// day is never a "last day", so day-tour guests are always included.

const PRESETS = [
    { label: 'Today', range: () => [moment().startOf('day'), moment().startOf('day')] },
    { label: 'Tomorrow', range: () => [moment().add(1, 'day').startOf('day'), moment().add(1, 'day').startOf('day')] },
    { label: 'Next 7 days', range: () => [moment().startOf('day'), moment().add(6, 'days').startOf('day')] },
];

export function activeDuringLabel(filter) {
    const [start, end] = filter?.range || [];
    if (!start) {
        return null;
    }
    const days = !end || start.isSame(end, 'day')
        ? start.format('MMM D')
        : `${start.format('MMM D')} – ${end.format('MMM D')}`;
    return `Active ${days}${filter.includeLastDay === false ? ' · excl. last days' : ''}`;
}

// Query params for the chat list endpoints (ChatViewset / ConversationsViewset).
export function activeDuringParams(filter) {
    const [start, end] = filter?.range || [];
    if (!start) {
        return {};
    }
    return {
        active_from: start.format('YYYY-MM-DD'),
        active_to: (end || start).format('YYYY-MM-DD'),
        active_include_last_day: filter.includeLastDay !== false,
    };
}

export default function DateFilterPopover({ setDateSearchFilter, activeDuring, setActiveDuring }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [mode, setMode] = React.useState(activeDuring?.[0] ? 'active' : 'departs');
    const [draft, setDraft] = React.useState(activeDuring?.range || [null, null]);
    const [includeLastDay, setIncludeLastDay] = React.useState(activeDuring?.includeLastDay ?? true);

    const open = Boolean(anchorEl);
    const close = () => setAnchorEl(null);
    const apply = (range) => {
        const [start, end] = range;
        setActiveDuring(start ? { range: [start, end || start], includeLastDay } : null);
        close();
    };

    return (
        <>
            <IconButton
                aria-label="Filter by date"
                onClick={(e) => {
                    setDraft(activeDuring?.range || [null, null]);
                    setIncludeLastDay(activeDuring?.includeLastDay ?? true);
                    setAnchorEl(e.currentTarget);
                }}
            >
                <CalendarIcon style={{ color: cv('accent', '#007AFF') }} />
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={close}
                disableScrollLock
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { borderRadius: '8px', mt: 1 } }}
            >
                <Box sx={{ p: 1.5, pb: 1, width: mode === 'active' ? 340 : 320 }}>
                    <ToggleButtonGroup
                        value={mode}
                        exclusive
                        size="small"
                        fullWidth
                        onChange={(_, v) => v && setMode(v)}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', py: 0.4 } }}
                    >
                        <ToggleButton value="departs">Departs on</ToggleButton>
                        <ToggleButton value="active">Active during</ToggleButton>
                    </ToggleButtonGroup>

                    {mode === 'departs' ? (
                        <>
                            <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 1, px: 0.5 }}>
                                Tours that depart on this day.
                            </Typography>
                            <DateCalendar
                                value={null}
                                onChange={(d) => {
                                    if (d) {
                                        setDateSearchFilter?.(moment(d).format('YYYY-MM-DD'));
                                        close();
                                    }
                                }}
                            />
                        </>
                    ) : (
                        <>
                            <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 1, px: 0.5 }}>
                                Guests on their tour or hotel stay on any of these days, by each guest's own dates.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, mt: 1, px: 0.5, flexWrap: 'wrap' }}>
                                {PRESETS.map((p) => (
                                    <Chip
                                        key={p.label}
                                        label={p.label}
                                        size="small"
                                        variant="outlined"
                                        clickable
                                        onClick={() => apply(p.range())}
                                        sx={{ borderColor: cv('accent', '#007AFF'), color: cv('accent', '#007AFF') }}
                                    />
                                ))}
                            </Box>
                            <DateRangeCalendar
                                calendars={1}
                                value={draft}
                                onChange={(v) => setDraft(v)}
                            />
                            <FormControlLabel
                                sx={{ ml: 0, px: 0.5, mt: -1, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={includeLastDay}
                                        onChange={(e) => setIncludeLastDay(e.target.checked)}
                                    />
                                }
                                label="Include guests on their last day"
                            />
                            <Typography variant="caption" component="div" sx={{ color: 'text.secondary', px: 0.5, mt: -0.5, mb: 1 }}>
                                Untick to leave out guests whose hotel stay or multi-day tour ends that day. Day-tour guests are always included.
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, px: 0.5 }}>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setDraft([null, null]);
                                        setIncludeLastDay(true);
                                        setActiveDuring(null);
                                    }}
                                >
                                    Clear
                                </Button>
                                <Button size="small" variant="contained" disabled={!draft[0]} onClick={() => apply(draft)}>
                                    Apply
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Popover>
        </>
    );
}
